/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AdHocDto } from '../models/AdHocDto';
import type { CommissionBatchDTO } from '../models/CommissionBatchDTO';
import type { CommissionData } from '../models/CommissionData';
import type { CommissionRecordDTO } from '../models/CommissionRecordDTO';
import type { CommissionStatementDTO } from '../models/CommissionStatementDTO';
import type { DownloadCsvDto } from '../models/DownloadCsvDto';
import type { IncomeDeductiblesDTO } from '../models/IncomeDeductiblesDTO';
import type { NewCommissionDataDTO } from '../models/NewCommissionDataDTO';
import type { NewIncomeDeductiblesDTO } from '../models/NewIncomeDeductiblesDTO';
import type { Object } from '../models/Object';
import type { ReportGeneratorDto } from '../models/ReportGeneratorDto';
import type { ReportLogDTO } from '../models/ReportLogDTO';
import type { ScheduleTimeDto } from '../models/ScheduleTimeDto';
import type { UpdateCommissionBatchDTO } from '../models/UpdateCommissionBatchDTO';
import type { ValidateTaxInvoiceDTO } from '../models/ValidateTaxInvoiceDTO';
import { request as __request } from '@openapi/request';

export class CommissionRecordsService {

    /**
     * @returns CommissionBatchDTO
     * @throws ApiError
     */
    public static async commissionRecords(): Promise<Array<CommissionBatchDTO>> {
        const result = await __request({
            method: 'GET',
            path: `/live/api/commission_records/all`,
        });
        return result.body;
    }

    /**
     * @param requestBody
     * @returns any
     * @throws ApiError
     */
    public static async validateNewCommissionData(
        requestBody: Array<CommissionData>,
    ): Promise<any> {
        const result = await __request({
            method: 'POST',
            path: `/live/api/commission_records/validate/new-commission-data`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * @param requestBody
     * @returns any
     * @throws ApiError
     */
    public static async validateNewCommissionDataWithRemuneration(
        requestBody: NewCommissionDataDTO,
    ): Promise<any> {
        const result = await __request({
            method: 'POST',
            path: `/live/api/commission_records/validate/new-commission-data-remuneration`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * @param requestBody
     * @returns any
     * @throws ApiError
     */
    public static async importNewCommissionData(
        requestBody: NewCommissionDataDTO,
    ): Promise<any> {
        const result = await __request({
            method: 'POST',
            path: `/live/api/commission_records/import/new-commission-data`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * @param requestBody
     * @returns any
     * @throws ApiError
     */
    public static async importNewCommissionDataViaHierarchySetup(
        requestBody: NewCommissionDataDTO,
    ): Promise<any> {
        const result = await __request({
            method: 'POST',
            path: `/live/api/commission_records/import/new-commission-data-hierarchy-setup`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * @param commissionOid
     * @returns any
     * @throws ApiError
     */
    public static async deleteCommissionRecords(
        commissionOid: string,
    ): Promise<any> {
        const result = await __request({
            method: 'DELETE',
            path: `/live/api/commission_records/${commissionOid}`,
        });
        return result.body;
    }

    /**
     * @returns any
     * @throws ApiError
     */
    public static async getReport(): Promise<any> {
        const result = await __request({
            method: 'GET',
            path: `/live/api/commission_records/report`,
        });
        return result.body;
    }

    /**
     * @param requestBody
     * @returns any
     * @throws ApiError
     */
    public static async setScheduleJobTime(
        requestBody: ScheduleTimeDto,
    ): Promise<any> {
        const result = await __request({
            method: 'POST',
            path: `/live/api/commission_records/setScheduleJobTime`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * @param requestBody
     * @returns any
     * @throws ApiError
     */
    public static async generateAdHocStatement(
        requestBody: AdHocDto,
    ): Promise<any> {
        const result = await __request({
            method: 'POST',
            path: `/live/api/commission_records/generateAdHocStatement`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * @returns any
     * @throws ApiError
     */
    public static async getScheduleJobTime(): Promise<any> {
        const result = await __request({
            method: 'GET',
            path: `/live/api/commission_records/getScheduleJobTime`,
        });
        return result.body;
    }

    /**
     * @param requestBody
     * @returns any
     * @throws ApiError
     */
    public static async downloadCsv(
        requestBody: DownloadCsvDto,
    ): Promise<any> {
        const result = await __request({
            method: 'POST',
            path: `/live/api/commission_records/downloadCsv`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * @param oid
     * @returns any
     * @throws ApiError
     */
    public static async getCommissionBatchCsv(
        oid: string,
    ): Promise<any> {
        const result = await __request({
            method: 'GET',
            path: `/live/api/commission_records/commission-batch-csv/${oid}`,
        });
        return result.body;
    }

    /**
     * @param requestBody
     * @returns any
     * @throws ApiError
     */
    public static async reportGenerator(
        requestBody: ReportGeneratorDto,
    ): Promise<any> {
        const result = await __request({
            method: 'POST',
            path: `/live/api/commission_records/reportGenerator`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * @returns CommissionStatementDTO
     * @throws ApiError
     */
    public static async reportList(): Promise<Array<CommissionStatementDTO>> {
        const result = await __request({
            method: 'GET',
            path: `/live/api/commission_records/reportList/all`,
        });
        return result.body;
    }

    /**
     * @param userOid
     * @returns CommissionStatementDTO
     * @throws ApiError
     */
    public static async reportListForUser(
        userOid: string,
    ): Promise<Array<CommissionStatementDTO>> {
        const result = await __request({
            method: 'GET',
            path: `/live/api/commission_records/reportList/${userOid}`,
        });
        return result.body;
    }

    /**
     * @param batchId
     * @returns CommissionStatementDTO
     * @throws ApiError
     */
    public static async getReportListByBatchId(
        batchId: number,
    ): Promise<Array<CommissionStatementDTO>> {
        const result = await __request({
            method: 'GET',
            path: `/live/api/commission_records/statementList/${batchId}`,
        });
        return result.body;
    }

    /**
     * @param batchId
     * @param valueDate
     * @returns any
     * @throws ApiError
     */
    public static async getGiroReport(
        batchId: number,
        valueDate: number,
    ): Promise<any> {
        const result = await __request({
            method: 'GET',
            path: `/live/api/commission_records/giro-report`,
            query: {
                'batchId': batchId,
                'valueDate': valueDate,
            },
        });
        return result.body;
    }

    /**
     * @param requestBody
     * @returns any
     * @throws ApiError
     */
    public static async updatePublishedStatus(
        requestBody: Object,
    ): Promise<any> {
        const result = await __request({
            method: 'PUT',
            path: `/live/api/commission_records/publishStatus`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * @returns any
     * @throws ApiError
     */
    public static async listOfPrincipalsAndUsers(): Promise<any> {
        const result = await __request({
            method: 'GET',
            path: `/live/api/commission_records/listOfPrincipalsAndUsers`,
        });
        return result.body;
    }

    /**
     * @returns any
     * @throws ApiError
     */
    public static async getDeductiblesDescription(): Promise<any> {
        const result = await __request({
            method: 'GET',
            path: `/live/api/commission_records/deductibles/description`,
        });
        return result.body;
    }

    /**
     * @returns IncomeDeductiblesDTO
     * @throws ApiError
     */
    public static async allIncomeDeductibles(): Promise<Array<IncomeDeductiblesDTO>> {
        const result = await __request({
            method: 'GET',
            path: `/live/api/commission_records/deductibles/all`,
        });
        return result.body;
    }

    /**
     * @param requestBody
     * @returns any
     * @throws ApiError
     */
    public static async addDeductibles(
        requestBody: Array<NewIncomeDeductiblesDTO>,
    ): Promise<any> {
        const result = await __request({
            method: 'POST',
            path: `/live/api/commission_records/deductibles`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * @param requestBody
     * @returns any
     * @throws ApiError
     */
    public static async updateDeductibles(
        requestBody: IncomeDeductiblesDTO,
    ): Promise<any> {
        const result = await __request({
            method: 'PUT',
            path: `/live/api/commission_records/deductibles`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * @param oid
     * @returns any
     * @throws ApiError
     */
    public static async deleteDeductibles(
        oid: string,
    ): Promise<any> {
        const result = await __request({
            method: 'DELETE',
            path: `/live/api/commission_records/deductibles/${oid}`,
        });
        return result.body;
    }

    /**
     * @returns ReportLogDTO
     * @throws ApiError
     */
    public static async getReportLog(): Promise<Array<ReportLogDTO>> {
        const result = await __request({
            method: 'GET',
            path: `/live/api/commission_records/report-log`,
        });
        return result.body;
    }

    /**
     * @param batchOid
     * @returns CommissionRecordDTO
     * @throws ApiError
     */
    public static async getCommissionRecordsByBatch(
        batchOid: string,
    ): Promise<Array<CommissionRecordDTO>> {
        const result = await __request({
            method: 'GET',
            path: `/live/api/commission_records/batch/${batchOid}`,
        });
        return result.body;
    }

    /**
     * @param policyNumber
     * @returns CommissionRecordDTO
     * @throws ApiError
     */
    public static async getCommissionRecordsByPolicyNumber(
        policyNumber: string,
    ): Promise<Array<CommissionRecordDTO>> {
        const result = await __request({
            method: 'GET',
            path: `/live/api/commission_records/${policyNumber}/policy`,
        });
        return result.body;
    }

    /**
     * @param policyNumber
     * @returns any
     * @throws ApiError
     */
    public static async downloadPolicyCommissionRecordsCsv(
        policyNumber: string,
    ): Promise<any> {
        const result = await __request({
            method: 'GET',
            path: `/live/api/commission_records/${policyNumber}/csv`,
        });
        return result.body;
    }

    /**
     * @param requestBody
     * @returns any
     * @throws ApiError
     */
    public static async updateCommissionBatch(
        requestBody: UpdateCommissionBatchDTO,
    ): Promise<any> {
        const result = await __request({
            method: 'PUT',
            path: `/live/api/commission_records/batch`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * @param requestBody
     * @returns any
     * @throws ApiError
     */
    public static async validateTaxInvoiceNo(
        requestBody: ValidateTaxInvoiceDTO,
    ): Promise<any> {
        const result = await __request({
            method: 'POST',
            path: `/live/api/commission_records/validate-tax-invoice-no`,
            body: requestBody,
        });
        return result.body;
    }

}